// One switch, used by every per-line feature card, that IS the state display.
//
// Kevin, 2026-08-19: "instead of the 'on' badge, it should be a slider that controls voicemail
// for the line while also showing the current state on/off" — and "same goes for messaging. the
// two tabs should look and feel the same" — and "if the feature is disabled because of the
// service package, it should be off and grayed out so that it cannot be changed."
//
// WHY SHARED RATHER THAN TWO THAT MATCH. The Voicemail tab had a badge and no control at all; the
// Messaging tab had a badge AND a pair of buttons underneath. Two surfaces for one kind of
// decision, drifting apart in wording and layout. A component both import cannot drift.
//
// THE SWITCH REPLACES THE BADGE, never sits beside it. Two things that both claim to say whether
// a feature is on is precisely how they end up disagreeing — which is the bug this product keeps
// producing (a badge reading the PACKAGE while the PBX answered `novm`, an "online" extension
// beside "cannot register", a mailbox drawn on a call path that did not exist).
//
// FOUR STATES, and the difference between them is the whole point:
//
//   on        — the far system says it is on, and it can be turned off
//   off       — the far system says it is off, and it can be turned on
//   locked    — off, greyed, and unchangeable, because something upstream forbids it: the service
//               package does not include the feature, or the account is not enabled for it. The
//               reason is shown, never a dead control with no explanation.
//   unknown   — the far system could not be reached. NOT rendered as "off": that is the same lie
//               pointing the other way, and an unreachable host has told us nothing.
//
// `confirm` makes turning OFF a two-step action where off is destructive — FreePBX's
// disableVoiceMail takes the mailbox and its stored messages with it. Turning ON never needs
// confirming; nothing is lost by gaining a feature.

import { useState } from 'react';

const TRACK = {
  on:      'bg-green-600',
  off:     'bg-gray-300',
  locked:  'bg-gray-200',
  unknown: 'bg-gray-200',
};
const WORD = { on: 'On', off: 'Off', locked: 'Off', unknown: 'Unknown' };
const WORD_TONE = {
  on: 'text-green-800',
  off: 'text-gray-600',
  locked: 'text-gray-400',
  unknown: 'text-gray-500',
};

export default function FeatureSwitch({
  state,              // 'on' | 'off' | 'locked' | 'unknown'
  label,              // accessible name, e.g. 'Voicemail for this line'
  onChange,           // (next: boolean) => void
  busy = false,
  canWrite = true,
  reason = null,      // why it is locked or unknown — shown beside the switch
  confirm = null,     // { title, body, confirmLabel } to require before turning OFF
  showState = true,   // false in list rows, where the row's own label already says what it is and
                      // an "On/Off" word repeated down twelve lines is noise
}) {
  const [pending, setPending] = useState(false);
  const fixed = state === 'locked' || state === 'unknown';
  const disabled = fixed || busy || !canWrite;
  const checked = state === 'on';

  const flip = () => {
    if (disabled) return;
    if (checked && confirm) { setPending(true); return; }
    onChange(!checked);
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        {showState && (
          <span className={`text-xs font-medium ${WORD_TONE[state]}`}>{busy ? 'Saving…' : WORD[state]}</span>
        )}
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label}
          disabled={disabled}
          title={reason || (!canWrite ? 'You don’t have permission to change this' : undefined)}
          onClick={flip}
          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors
            ${TRACK[state]} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all
              ${checked ? 'left-[1.125rem]' : 'left-0.5'}`}
          />
        </button>
      </div>
      {/* The reason travels with the control. A greyed switch with no explanation is the thing
          an operator files a ticket about. */}
      {reason && <span className="max-w-[16rem] text-right text-[11px] leading-tight text-gray-500">{reason}</span>}

      {pending && confirm && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">{confirm.title}</h2>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
              {confirm.body}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setPending(false); onChange(false); }}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  {confirm.confirmLabel || 'Turn it off'}
                </button>
                <button
                  type="button"
                  onClick={() => setPending(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
